import {
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Tooltip,
} from '@mui/material';
import { AppNumberInput, FieldRow } from 'components/FormElements';
import PageSection from 'components/PageSection';
import { CurrencyCode, CommitmentType } from 'further-types/lp/vehicle';
import { CapitalContributionType } from 'further-types/lp/carry-settings';
import { Controller, useForm } from 'react-hook-form';
import { carrySettings } from 'further-ui/validations';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import ButtonGroup from 'components/ButtonGroup';
import Button from 'components/Button';
import Spacing from 'components/Spacing';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { getCurrencySymbol, numberToCurrencyString } from 'further-ui/utils';
import { round } from 'lodash';
import { usePagination } from 'further-ui/hooks';

type FormValues = z.infer<typeof carrySettings.CarriedInterestPartnerSettings>;

type Props = {
  vehicle: {
    _id: string;
    baseCurrency: CurrencyCode;
    commitmentType?: CommitmentType;
    fixedCapitalAmount?: number | null;
    carrySettings: {
      carryPartnerName?: string | null;
      carryPartnerFundCommitment?: number;
      capitalContributionType?: CapitalContributionType | null;
      capitalContributionAmountLcy?: number | null;
      capitalContributionPercentage?: number | null;
      loanCommitmentLcy?: number | null;
    };
  };
};

const calculateCapitalContributionAmountLcy = (
  vehicleFixedCapital: number,
  capitalContributionPercentage: number,
) => {
  return round((vehicleFixedCapital * capitalContributionPercentage) / 100, 2);
};

const CarriedInterestPartnerSettings: React.FC<Props> = ({ vehicle }) => {
  const pagination = usePagination({
    id: 'carried-interest-partner-settings',
    orderBy: 'commitmentDate',
    order: 'desc',
  });

  const [commitmentsData] = api.commitments.list.useSuspenseQuery({
    vehicleId: vehicle?._id ?? '',
    onlyEqualised: true,
    ...pagination.properties,
  });

  const isCarryPartnerEqualised =
    commitmentsData?.results?.some(
      (commitment) => commitment.isCarryPartnerCommitment,
    ) ?? false;

  const isCapitalAndLoan =
    vehicle.commitmentType === CommitmentType.CapitalAndLoan;

  const vehicleFixedCapital = vehicle.fixedCapitalAmount;
  const initialCapitalContributionAmountLcy =
    vehicle.carrySettings.capitalContributionType ===
      CapitalContributionType.PercentageOfTotalCapital &&
    vehicleFixedCapital != null &&
    vehicle.carrySettings.capitalContributionPercentage != null
      ? calculateCapitalContributionAmountLcy(
          vehicleFixedCapital,
          vehicle.carrySettings.capitalContributionPercentage,
        )
      : vehicle.carrySettings.capitalContributionAmountLcy ?? null;

  const { control, handleSubmit, setValue, watch } = useForm({
    resolver: zodResolver(carrySettings.CarriedInterestPartnerSettings),
    shouldFocusError: true,
    values: {
      carryPartnerName:
        vehicle.carrySettings.carryPartnerName ?? 'Carry partner',
      ...(isCapitalAndLoan
        ? {
            capitalContributionType:
              vehicle.carrySettings.capitalContributionType ??
              CapitalContributionType.FixedAmount,
            capitalContributionAmountLcy: initialCapitalContributionAmountLcy,
            capitalContributionPercentage:
              vehicle.carrySettings.capitalContributionPercentage ?? null,
            loanCommitmentLcy: vehicle.carrySettings.loanCommitmentLcy ?? null,
            carryPartnerFundCommitment: 0,
          }
        : {
            capitalContributionType: null,
            capitalContributionAmountLcy: null,
            capitalContributionPercentage: null,
            loanCommitmentLcy: null,
            carryPartnerFundCommitment:
              vehicle.carrySettings.carryPartnerFundCommitment ?? 0,
          }),
    },
    criteriaMode: 'all',
  });

  const options = useHandleTrpcMutation()({
    successMessage: 'Carried interest partner settings updated.',
  });

  const { mutate: updateCarriedInterestPartnerSettings, isPending } =
    api.vehicle.updateCarriedInterestPartnerSettings.useMutation(options);

  const onSubmit = (data: FormValues) => {
    const dataToSubmit = isCarryPartnerEqualised
      ? { carryPartnerName: data.carryPartnerName }
      : {
          ...data,
          ...(data.capitalContributionType ===
          CapitalContributionType.FixedAmount
            ? {
                capitalContributionPercentage: null,
              }
            : {
                capitalContributionAmountLcy: null,
              }),
        };

    updateCarriedInterestPartnerSettings({
      vehicleId: vehicle._id,
      ...dataToSubmit,
    });
  };

  const currencySymbol = getCurrencySymbol(vehicle.baseCurrency);
  const capitalContributionType = watch('capitalContributionType');
  const capitalContributionAmountLcy = watch('capitalContributionAmountLcy');
  const capitalContributionPercentage = watch('capitalContributionPercentage');
  const loanCommitmentLcy = watch('loanCommitmentLcy');

  const totalCommitment =
    capitalContributionType ===
      CapitalContributionType.PercentageOfTotalCapital && !vehicleFixedCapital
      ? 'TBC'
      : numberToCurrencyString(
          (capitalContributionAmountLcy ?? 0) + (loanCommitmentLcy ?? 0),
          {
            currency: vehicle.baseCurrency,
          },
        );

  const equalisedTooltipText =
    "As the fund has completed its first close, the carry partner's total commitment can only be adjusted in the Close & Equalise feature when processing a subsequent close.";

  return (
    <PageSection
      headerContent={
        <Typography variant="h4">Carried interest partner settings</Typography>
      }
      withSpacing
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Spacing>
          <FieldRow title="Carry partner name" centerTitle>
            <Controller
              name="carryPartnerName"
              control={control}
              render={({ fieldState: { error }, field }) => (
                <TextField
                  {...field}
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  size="small"
                  style={{
                    ...(isCapitalAndLoan ? { marginLeft: 12, width: 400 } : {}),
                  }}
                />
              )}
            />
          </FieldRow>
          {isCapitalAndLoan ? (
            <>
              <FieldRow
                title="Capital contribution type"
                centerTitle
                tooltipText={
                  !isCarryPartnerEqualised
                    ? `Enter a fixed sum or percentage of total fund capital. Where a percentage is used, the amount of capital in ${currencySymbol} will change automatically if total fund capital changes.`
                    : undefined
                }
              >
                {isCarryPartnerEqualised ? (
                  <Typography variant="h6" style={{ marginLeft: 12 }}>
                    {capitalContributionType ===
                    CapitalContributionType.FixedAmount
                      ? 'Fixed amount'
                      : 'Percentage of total capital'}
                  </Typography>
                ) : (
                  <Controller
                    name="capitalContributionType"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <RadioGroup
                        row
                        value={value}
                        onChange={(e) => {
                          const nextType =
                            e.target.value ===
                            CapitalContributionType.PercentageOfTotalCapital
                              ? CapitalContributionType.PercentageOfTotalCapital
                              : CapitalContributionType.FixedAmount;

                          onChange(nextType);

                          if (
                            nextType ===
                              CapitalContributionType.PercentageOfTotalCapital &&
                            vehicleFixedCapital != null &&
                            capitalContributionPercentage != null
                          ) {
                            setValue(
                              'capitalContributionAmountLcy',
                              calculateCapitalContributionAmountLcy(
                                vehicleFixedCapital,
                                capitalContributionPercentage,
                              ),
                            );
                          }
                        }}
                      >
                        <FormControlLabel
                          value={CapitalContributionType.FixedAmount}
                          control={<Radio />}
                          label={
                            <Typography variant="h6">Fixed amount</Typography>
                          }
                        />
                        <FormControlLabel
                          value={
                            CapitalContributionType.PercentageOfTotalCapital
                          }
                          control={<Radio />}
                          label={
                            <Typography variant="h6">
                              Percentage of total capital
                            </Typography>
                          }
                        />
                      </RadioGroup>
                    )}
                  />
                )}
              </FieldRow>

              {capitalContributionType ===
                CapitalContributionType.FixedAmount && (
                <FieldRow title="Capital contribution amount" centerTitle>
                  <Controller
                    name="capitalContributionAmountLcy"
                    control={control}
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => {
                      const input = (
                        <AppNumberInput
                          value={value ?? undefined}
                          onChange={onChange}
                          min={0}
                          decimals={2}
                          minFractionDigits={2}
                          helperText={error?.message?.toString()}
                          error={!!error}
                          currencyStartAdornment
                          currency={vehicle.baseCurrency}
                          disabled={isCarryPartnerEqualised}
                          style={{ marginLeft: 12, width: 200 }}
                        />
                      );

                      return isCarryPartnerEqualised ? (
                        <Tooltip title={equalisedTooltipText}>
                          <span>{input}</span>
                        </Tooltip>
                      ) : (
                        input
                      );
                    }}
                  />
                </FieldRow>
              )}
              {capitalContributionType ===
                CapitalContributionType.PercentageOfTotalCapital && (
                <>
                  <FieldRow title="Capital contribution amount (%)" centerTitle>
                    <Controller
                      name="capitalContributionPercentage"
                      control={control}
                      render={({
                        field: { value, onChange },
                        fieldState: { error },
                      }) => {
                        const input = (
                          <AppNumberInput
                            value={value ?? undefined}
                            onChange={(nextPercentage: number) => {
                              onChange(nextPercentage);

                              if (vehicleFixedCapital != null) {
                                setValue(
                                  'capitalContributionAmountLcy',
                                  calculateCapitalContributionAmountLcy(
                                    vehicleFixedCapital,
                                    nextPercentage,
                                  ),
                                );
                              }
                            }}
                            min={0}
                            max={100}
                            decimals={10}
                            minFractionDigits={2}
                            helperText={error?.message?.toString()}
                            error={!!error}
                            percentEndAdornment
                            disabled={isCarryPartnerEqualised}
                            style={{ marginLeft: 12, width: 200 }}
                          />
                        );

                        return isCarryPartnerEqualised ? (
                          <Tooltip title={equalisedTooltipText}>
                            <span>{input}</span>
                          </Tooltip>
                        ) : (
                          input
                        );
                      }}
                    />
                  </FieldRow>
                  <FieldRow
                    title="Capital contribution amount"
                    centerTitle
                    tooltipText={
                      vehicleFixedCapital
                        ? undefined
                        : 'The exact capital contribution amount will be calculated as part of each fund close and will display here after the first close. It is subject to change until the final fund close.'
                    }
                  >
                    {vehicleFixedCapital ? (
                      <Controller
                        name="capitalContributionAmountLcy"
                        control={control}
                        render={({
                          field: { value },
                          fieldState: { error },
                        }) => (
                          <AppNumberInput
                            value={value ?? undefined}
                            onChange={() => {}}
                            min={0}
                            decimals={2}
                            minFractionDigits={2}
                            helperText={error?.message?.toString()}
                            error={!!error}
                            currencyStartAdornment
                            currency={vehicle.baseCurrency}
                            disabled
                            style={{ marginLeft: 12, width: 200 }}
                          />
                        )}
                      />
                    ) : (
                      <Typography variant="h6" style={{ marginLeft: 12 }}>
                        {totalCommitment}
                      </Typography>
                    )}
                  </FieldRow>
                </>
              )}

              <FieldRow title="Loan commitment" centerTitle>
                <Controller
                  name="loanCommitmentLcy"
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => {
                    const input = (
                      <AppNumberInput
                        value={value ?? undefined}
                        onChange={onChange}
                        min={0}
                        decimals={2}
                        minFractionDigits={2}
                        helperText={error?.message?.toString()}
                        error={!!error}
                        currencyStartAdornment
                        currency={vehicle.baseCurrency}
                        disabled={isCarryPartnerEqualised}
                        style={{ marginLeft: 12, width: 200 }}
                      />
                    );

                    return isCarryPartnerEqualised ? (
                      <Tooltip title={equalisedTooltipText}>
                        <span>{input}</span>
                      </Tooltip>
                    ) : (
                      input
                    );
                  }}
                />
              </FieldRow>

              <FieldRow
                title="Carry partner total commitment"
                centerTitle
                tooltipText={
                  !vehicleFixedCapital
                    ? 'The exact total commitment will be calculated as part of each fund close and will display here after the first close. It is subject to change until the final fund close.'
                    : undefined
                }
              >
                <Typography variant="h6" style={{ marginLeft: 12 }}>
                  {totalCommitment}
                </Typography>
              </FieldRow>
            </>
          ) : (
            <FieldRow title="Carry partner commitment" centerTitle>
              <Controller
                name="carryPartnerFundCommitment"
                control={control}
                render={({
                  fieldState: { error },
                  field: { onChange, ...rest },
                }) => {
                  const input = (
                    <AppNumberInput
                      {...rest}
                      decimals={2}
                      minFractionDigits={2}
                      onChange={(value: number) => {
                        onChange(value);
                      }}
                      error={!!error}
                      helperText={error?.message}
                      currencyStartAdornment
                      currency={vehicle?.baseCurrency}
                      disabled={isCarryPartnerEqualised}
                    />
                  );

                  return isCarryPartnerEqualised ? (
                    <Tooltip title={equalisedTooltipText}>
                      <span>{input}</span>
                    </Tooltip>
                  ) : (
                    input
                  );
                }}
              />
            </FieldRow>
          )}
          <ButtonGroup>
            <Button
              size="large"
              loading={isPending}
              variant="contained"
              type="submit"
            >
              Save changes
            </Button>
          </ButtonGroup>
        </Spacing>
      </form>
    </PageSection>
  );
};

export default CarriedInterestPartnerSettings;
